/**
 * Audio Player Context
 * Manages global audio playback state without Redux
 * Source of truth: ref-files/src/react-app/contexts/AudioPlayerContext.tsx
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export interface AudioTrack {
  id: string;
  title: string;
  artist: string;
  url: string;
  duration?: number;
  albumArt?: string;
  playlistId?: string;
  position?: number;
}

export interface AudioPlayerState {
  currentTrack: AudioTrack | null;
  isPlaying: boolean;
  isLoading: boolean;
  currentTime: number;
  duration: number;
  buffered: number;
  volume: number;
  isMuted: boolean;
  playbackRate: number;
  playlist: AudioTrack[];
  currentIndex: number;
  shuffle: boolean;
  repeat: 'none' | 'one' | 'all';
  showPlayer: boolean;
  error: string | null;
  isDragging: boolean;
}

interface AudioPlayerContextType {
  state: AudioPlayerState;
  playTrack: (track: AudioTrack) => void;
  play: () => void;
  pause: () => void;
  setCurrentTime: (time: number) => void;
  setDuration: (duration: number) => void;
  setBuffered: (buffered: number) => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;
  setPlaybackRate: (rate: number) => void;
  setPlaylist: (tracks: AudioTrack[]) => void;
  nextTrack: () => void;
  previousTrack: () => void;
  toggleShuffle: () => void;
  setRepeat: (repeat: 'none' | 'one' | 'all') => void;
  setError: (error: string | null) => void;
  setLoading: (loading: boolean) => void;
  setIsDragging: (isDragging: boolean) => void;
  seekTo: (time: number) => void;
  trackEnded: () => void;
  hidePlayer: () => void;
  setAudioElement: (audio: HTMLAudioElement | null) => void;
  // Legacy aliases for backward compatibility
  currentTrack: AudioTrack | null;
  isPlaying: boolean;
  isLoading: boolean;
  currentTime: number;
  duration: number;
  setCurrentTrack: (track: AudioTrack | null) => void;
  setIsPlaying: (playing: boolean) => void;
  playNext: () => void;
  playPrevious: () => void;
  setQueue: (tracks: AudioTrack[]) => void;
}

const AudioPlayerContext = createContext<AudioPlayerContextType | undefined>(undefined);

const initialState: AudioPlayerState = {
  currentTrack: null,
  isPlaying: false,
  isLoading: false,
  currentTime: 0,
  duration: 0,
  buffered: 0,
  volume: 1,
  isMuted: false,
  playbackRate: 1,
  playlist: [],
  currentIndex: -1,
  shuffle: false,
  repeat: 'none',
  showPlayer: false,
  error: null,
  isDragging: false,
};

export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error('useAudioPlayer must be used within an AudioPlayerProvider');
  }
  return context;
};

interface AudioPlayerProviderProps {
  children: ReactNode;
}

export const AudioPlayerProvider: React.FC<AudioPlayerProviderProps> = ({ children }) => {
  const [state, setState] = useState<AudioPlayerState>(() => {
    // Initialize volume from localStorage on mount
    const savedVolume = localStorage.getItem('audioPlayerVolume');
    if (savedVolume !== null) {
      const vol = parseFloat(savedVolume);
      if (vol >= 0 && vol <= 1) {
        return { ...initialState, volume: vol };
      }
    }
    return initialState;
  });
  const audioElementRef = React.useRef<HTMLAudioElement | null>(null);

  const setAudioElement = useCallback((audio: HTMLAudioElement | null) => {
    audioElementRef.current = audio;
  }, []);

  const seekTo = useCallback((time: number) => {
    if (audioElementRef.current) {
      audioElementRef.current.currentTime = time;
    }
    setState(prev => {
      if (Math.abs(prev.currentTime - time) > 0.01) {
        return { ...prev, currentTime: time };
      }
      return prev;
    });
  }, []);

  const playTrack = useCallback((track: AudioTrack) => {
    setState(prev => ({
      ...prev,
      currentTrack: track,
      isPlaying: true,
      isLoading: true,
      currentTime: 0,
      duration: track.duration || 0,
      error: null,
      showPlayer: true,
    }));
  }, []);

  const play = useCallback(() => {
    setState(prev => ({
      ...prev,
      isPlaying: prev.currentTrack ? true : false,
      isLoading: false,
    }));
  }, []);

  const pause = useCallback(() => {
    setState(prev => ({ ...prev, isPlaying: false }));
  }, []);

  const setCurrentTime = useCallback((time: number) => {
    setState(prev => ({ ...prev, currentTime: time }));
  }, []);

  const setDuration = useCallback((duration: number) => {
    setState(prev => ({ ...prev, duration }));
  }, []);

  const setBuffered = useCallback((buffered: number) => {
    setState(prev => ({ ...prev, buffered }));
  }, []);

  const setVolume = useCallback((volume: number) => {
    const clampedVolume = Math.max(0, Math.min(1, volume));
    // Persist volume to localStorage
    localStorage.setItem('audioPlayerVolume', String(clampedVolume));
    setState(prev => ({
      ...prev,
      volume: clampedVolume,
      isMuted: clampedVolume === 0 ? prev.isMuted : false,
    }));
  }, []);

  const toggleMute = useCallback(() => {
    setState(prev => ({ ...prev, isMuted: !prev.isMuted }));
  }, []);

  const setPlaybackRate = useCallback((rate: number) => {
    setState(prev => ({ ...prev, playbackRate: rate }));
  }, []);

  const setPlaylist = useCallback((tracks: AudioTrack[]) => {
    setState(prev => ({ ...prev, playlist: tracks, currentIndex: -1 }));
  }, []);

  const nextTrack = useCallback(() => {
    setState(prev => {
      if (prev.playlist.length === 0) return prev;
      let nextIndex = prev.currentIndex + 1;
      if (prev.shuffle) {
        const availableIndices = prev.playlist.map((_, i) => i).filter(i => i !== prev.currentIndex);
        nextIndex = availableIndices[Math.floor(Math.random() * availableIndices.length)];
      } else if (nextIndex >= prev.playlist.length) {
        if (prev.repeat === 'all') nextIndex = 0;
        else return prev;
      }
      return {
        ...prev,
        currentIndex: nextIndex,
        currentTrack: prev.playlist[nextIndex],
        currentTime: 0,
        isPlaying: true,
        isLoading: true,
      };
    });
  }, []);

  const previousTrack = useCallback(() => {
    setState(prev => {
      if (prev.playlist.length === 0) return prev;
      let prevIndex = prev.currentIndex - 1;
      if (prevIndex < 0) {
        if (prev.repeat === 'all') prevIndex = prev.playlist.length - 1;
        else return prev;
      }
      return {
        ...prev,
        currentIndex: prevIndex,
        currentTrack: prev.playlist[prevIndex],
        currentTime: 0,
        isPlaying: true,
        isLoading: true,
      };
    });
  }, []);

  const toggleShuffle = useCallback(() => {
    setState(prev => ({ ...prev, shuffle: !prev.shuffle }));
  }, []);

  const setRepeat = useCallback((repeat: 'none' | 'one' | 'all') => {
    setState(prev => ({ ...prev, repeat }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState(prev => ({
      ...prev,
      error,
      isLoading: error ? false : prev.isLoading,
      isPlaying: error ? false : prev.isPlaying,
    }));
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    setState(prev => ({ ...prev, isLoading: loading }));
  }, []);

  const setIsDragging = useCallback((isDragging: boolean) => {
    setState(prev => ({ ...prev, isDragging }));
  }, []);

  const trackEnded = useCallback(() => {
    setState(prev => {
      if (prev.repeat === 'one') {
        return { ...prev, currentTime: 0 };
      }
      if (prev.playlist.length === 0) return { ...prev, isPlaying: false };
      let nextIndex = prev.currentIndex + 1;
      if (nextIndex >= prev.playlist.length) {
        if (prev.repeat === 'all') nextIndex = 0;
        else return { ...prev, isPlaying: false };
      }
      return {
        ...prev,
        currentIndex: nextIndex,
        currentTrack: prev.playlist[nextIndex],
        currentTime: 0,
        isPlaying: true,
        isLoading: true,
      };
    });
  }, []);

  const hidePlayer = useCallback(() => {
    setState(prev => ({ ...prev, showPlayer: false }));
  }, []);

  // Backward-compatible setCurrentTrack that also updates state
  const setCurrentTrack = useCallback((track: AudioTrack | null) => {
    setState(prev => ({
      ...prev,
      currentTrack: track,
      showPlayer: track ? true : prev.showPlayer,
    }));
  }, []);

  const setIsPlaying = useCallback((playing: boolean) => {
    setState(prev => ({ ...prev, isPlaying: playing }));
  }, []);

  // Legacy setQueue alias — sets playlist while preserving current track index
  const setQueue = useCallback((tracks: AudioTrack[]) => {
    setState(prev => {
      const current = prev.currentTrack;
      let currentIndex = -1;
      if (current) {
        currentIndex = tracks.findIndex(t => t.id === current.id);
      }
      return { ...prev, playlist: tracks, currentIndex };
    });
  }, []);

  const value: AudioPlayerContextType = {
    state,
    playTrack,
    play,
    pause,
    setCurrentTime,
    setDuration,
    setBuffered,
    setVolume,
    toggleMute,
    setPlaybackRate,
    setPlaylist,
    nextTrack,
    previousTrack,
    toggleShuffle,
    setRepeat,
    setError,
    setLoading,
    setIsDragging,
    seekTo,
    trackEnded,
    hidePlayer,
    setAudioElement,
    // Legacy aliases for backward compatibility
    currentTrack: state.currentTrack,
    isPlaying: state.isPlaying,
    isLoading: state.isLoading,
    currentTime: state.currentTime,
    duration: state.duration,
    setCurrentTrack,
    setIsPlaying,
    playNext: nextTrack,
    playPrevious: previousTrack,
    setQueue,
  };

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
    </AudioPlayerContext.Provider>
  );
};

export default AudioPlayerProvider;
